import {
  useEffect,
  useState,
  type PointerEvent as ReactPointerEvent,
} from "react";
import Button from "../../../shared/components/Button";
import { Accordion } from "../../../shared/components/Accordion";
import { getWorkflowNodeLogs, type WorkflowNodeLog } from "../api/workflow-api";
import { NodeInspector } from "./NodeInspector";
import { NodePalette } from "./NodePalette";
import type { NodeDefinition, WorkflowNode } from "../model";
import "./WorkflowSidebar.scss";

type WorkflowSidebarProps = {
  definitions: NodeDefinition[];
  onAddNode: (definition: NodeDefinition) => void;
  onClose: () => void;
  onOpenConfig: (nodeID: string) => void;
  onResizeStart: (event: ReactPointerEvent<HTMLDivElement>) => void;
  selectedNode: WorkflowNode | null;
  upstreamNodes: WorkflowNode[];
  running: boolean;
  activityVersion: number;
};

export function WorkflowSidebar({
  definitions,
  onAddNode,
  onClose,
  onOpenConfig,
  onResizeStart,
  selectedNode,
  upstreamNodes,
  running,
  activityVersion,
}: WorkflowSidebarProps) {
  const [logs, setLogs] = useState<WorkflowNodeLog[]>([]);
  const [logsError, setLogsError] = useState("");

  useEffect(() => {
    if (!selectedNode) return;
    let active = true;
    getWorkflowNodeLogs(selectedNode.id)
      .then((items) => active && setLogs(items))
      .catch(() => active && setLogsError("Backend activity unavailable."));
    return () => {
      active = false;
    };
  }, [selectedNode]);

  return (
    <aside className="inspector">
      <div
        aria-label="Resize node inspector"
        className="inspector-resize-handle"
        onPointerDown={onResizeStart}
        role="separator"
      />
      <div className="inspector-toolbar">
        <p className="inspector-label">Workflow panel</p>
        <Button aria-label="Close inspector" onClick={onClose} variant="ghost">
          Close
        </Button>
      </div>
      <div className="inspector-content">
        <Accordion defaultOpen title={selectedNode ? selectedNode.title : "Node Inspector"}>
          <NodeInspector selectedNode={selectedNode} upstreamNodes={upstreamNodes} />
          {selectedNode && (
            <Button fullWidth onClick={() => onOpenConfig(selectedNode.id)}>
              Configure Node
            </Button>
          )}
        </Accordion>
        <NodePalette definitions={definitions} onAdd={onAddNode} />
        <Accordion title={selectedNode ? `${selectedNode.title} Activity` : "Activity"}>
          {selectedNode ? (
            <>
              <div className="inspector-activity-summary">
                <span>{running ? "Running" : "Stopped"}</span>
                <strong>{activityVersion} updates</strong>
              </div>
              {logsError ? (
                <p className="inspector-empty">{logsError}</p>
              ) : logs.length ? (
                <ol className="inspector-log-list">
                  {logs.map((log, index) => (
                    <li key={`${log.timestamp}-${index}`}>
                      <time>{new Date(log.timestamp).toLocaleTimeString()}</time>
                      <span className={`inspector-log-level inspector-log-level--${log.level}`}>
                        {log.level}
                      </span>
                      <p>{log.message}</p>
                    </li>
                  ))}
                </ol>
              ) : (
                <p className="inspector-empty">No backend activity yet.</p>
              )}
            </>
          ) : (
            <p className="inspector-empty">Select a node to view its activity.</p>
          )}
        </Accordion>
      </div>
    </aside>
  );
}
